import { useCallback, useEffect, useImperativeHandle, useRef, useState, type PointerEvent as ReactPointerEvent, type Ref } from 'react';
import { PenIcon } from './icons';

export interface SignaturePadHandle {
  clear: () => void;
  undo: () => void;
  isEmpty: () => boolean;
  toBlob: () => Promise<Blob | null>;
}

export interface SignaturePadProps {
  ref?: Ref<SignaturePadHandle>;
  onChange?: (hasInk: boolean) => void;
  height?: number;
}

type Point = { x: number; y: number };

/**
 * Touch + mouse signature capture on canvas. Strokes are kept as vectors so
 * Undo removes the last stroke exactly; the result exports as a PNG blob.
 */
export function SignaturePad({ ref, onChange, height = 176 }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Point[][]>([]);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const sync = useCallback(() => {
    const ink = strokes.current.length > 0;
    setHasInk(ink);
    onChange?.(ink);
  }, [onChange]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const styles = getComputedStyle(canvas);
    ctx.strokeStyle = styles.color || styles.getPropertyValue('--color-graphite');
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokes.current) {
      const first = stroke[0];
      if (!first) continue;
      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (let i = 1; i < stroke.length; i++) {
        const p = stroke[i];
        if (p) ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
    }
  }, []);

  useEffect(() => {
    redraw();
    const onResize = () => redraw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [redraw]);

  const localPoint = (event: ReactPointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      /* synthetic events (automation) may not support pointer capture */
    }
    drawing.current = true;
    strokes.current.push([localPoint(event)]);
    setHasInk(true);
    onChange?.(true);
    redraw();
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    event.preventDefault();
    const stroke = strokes.current[strokes.current.length - 1];
    if (!stroke) return;
    stroke.push(localPoint(event));
    redraw();
  };

  const endStroke = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* pointer already released */
    }
    redraw();
  };

  useImperativeHandle(
    ref,
    () => ({
      clear: () => {
        strokes.current = [];
        redraw();
        sync();
      },
      undo: () => {
        strokes.current.pop();
        redraw();
        sync();
      },
      isEmpty: () => strokes.current.length === 0,
      toBlob: () =>
        new Promise<Blob | null>((resolve) => {
          if (strokes.current.length === 0) {
            resolve(null);
            return;
          }
          canvasRef.current?.toBlob((blob) => resolve(blob), 'image/png');
        }),
    }),
    [redraw, sync],
  );

  return (
    <div className="sig" data-testid="pod-signature">
      <canvas
        ref={canvasRef}
        className="sig__canvas"
        style={{ height }}
        aria-label="Signature drawing area"
        role="img"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
        onPointerLeave={endStroke}
      />
      {!hasInk ? (
        <span className="sig__placeholder" aria-hidden="true">
          <PenIcon size={18} />
          Sign here
        </span>
      ) : null}
      <div className="sig__actions">
        <button
          type="button"
          className="u-btn u-btn--ghost u-btn--sm"
          onClick={() => {
            strokes.current.pop();
            redraw();
            sync();
          }}
          disabled={!hasInk}
          data-testid="pod-signature-undo"
        >
          Undo
        </button>
        <button
          type="button"
          className="u-btn u-btn--ghost u-btn--sm"
          onClick={() => {
            strokes.current = [];
            redraw();
            sync();
          }}
          disabled={!hasInk}
          data-testid="pod-signature-clear"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
